import React, { useEffect, useMemo, useState } from "react";
import { mockScreenData } from "../services/mockData";
import { ScreenData } from "../types/ScreenTypes";
import { PrayerTimes } from "../types/PrayerTypes";
import SimpleUpcomingSalahChangesSlideshow from "../components/elements/SimpleUpcomingSalahChangesSlideshow";
import SimpleUpcomingEventsWidget from "../components/elements/SimpleUpcomingEventsWidget";
import LabbaikSlide from "../components/slideshow/LabbaikSlide";
import { LabbaikItem } from "../hooks/useLabbaikItems";
import "../components/slideshow/SlideshowPage.css";

// Colour preview for the admin screen page: the real panels with sample content, recoloured
// live from the admin page's unsaved picks. No screen login and no masjid data.

type Panel = "salahChanges" | "events" | "labbaik" | "footer";
const PANELS: Panel[] = ["salahChanges", "events", "labbaik", "footer"];

const COLOUR_FIELDS = {
  primary: "primaryColor",
  secondary: "secondaryColor",
  accent: "accentColor",
  widget: "widgetBackgroundColor",
  heading: "headingTextColor",
} as const;
type ColourKey = keyof typeof COLOUR_FIELDS;
type Colours = Partial<Record<ColourKey, string>>;

const HEX = /^#[0-9a-f]{6}$/i;
const MESSAGE_TYPE = "m247-theme-preview";
const NEVER_ADVANCE = 24 * 60 * 60;
const noop = () => {};

function cleanColours(input: Record<string, unknown>): Colours {
  const colours: Colours = {};
  (Object.keys(COLOUR_FIELDS) as ColourKey[]).forEach((key) => {
    const raw = input[key];
    if (typeof raw !== "string") return;
    const value = raw.startsWith("#") ? raw : `#${raw}`;
    if (HEX.test(value)) colours[key] = value;
  });
  return colours;
}

function readQuery() {
  const params = new URLSearchParams(window.location.search);
  const panel = params.get("panel") as Panel;
  return {
    panel: PANELS.includes(panel) ? panel : "salahChanges",
    colours: cleanColours(Object.fromEntries(params.entries())),
  };
}

function sampleDay(offset: number, overrides: Partial<PrayerTimes>): PrayerTimes {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  return {
    Date: String(date.getDate()),
    Month: String(date.getMonth() + 1),
    Year: String(date.getFullYear()),
    SehriEnd: "05:40",
    BegFajr: "05:50",
    JamFajr: "06:30",
    Sunrise: "07:20",
    Noon: "12:35",
    BegDhuhr: "12:50",
    JamDhuhr: "13:30",
    BegAsar: "15:00",
    BegAsar2: "15:40",
    JamAsar: "16:00",
    Sunset: "17:50",
    BegMaghrib: "17:53",
    JamMaghrib: "17:58",
    BegEsha: "19:15",
    JamEsha: "19:45",
    HijriDate: "",
    GregorianDate: "",
    ...overrides,
  };
}

// A week starting today with three jamaat changes, so the panel always has something to show.
function sampleTimetable() {
  return [0, 1, 2, 3, 4, 5, 6].map((offset) =>
    sampleDay(offset, {
      ...(offset >= 2 && { JamFajr: "06:15" }),
      ...(offset >= 3 && { JamAsar: "15:30" }),
      ...(offset >= 4 && { JamEsha: "19:30" }),
    }),
  );
}

function at(daysAhead: number, hour: number, minute: number) {
  const date = new Date();
  date.setDate(date.getDate() + daysAhead);
  date.setHours(hour, minute, 0, 0);
  return date;
}

function sampleEvents() {
  return [
    { uid: "sample-1", summary: "Chai & Chat", location: "Madrassah Hall", audience: "Brothers", start: at(1, 16, 57), end: at(1, 18, 0) },
    { uid: "sample-2", summary: "Weekly Tafseer of the Last Juz", location: "Main hall", audience: "All welcome", start: at(1, 19, 30), end: at(1, 20, 30) },
    { uid: "sample-3", summary: "Family fun day", location: "Masjid car park", audience: "Families", start: at(2, 11, 0), end: at(2, 15, 0) },
  ];
}

const SAMPLE_LABBAIK: LabbaikItem = {
  kind: "appeal",
  title: "Roof appeal",
  text: "Help us fix the main hall roof before winter.",
  detail: "£1,700 raised of £5,000",
  progressPercent: 34,
  linkUrl: "https://uselabbaik.com",
  linkLabel: "Scan to donate",
  note: "Zakat eligible",
};

const SAMPLE_FOOTER = [
  ["Fajr", "6:45"],
  ["Dhuhr", "1:30"],
  ["Asr", "4:30"],
  ["Maghrib", "5:53"],
  ["Isha", "7:30"],
];

export default function PreviewPage() {
  const initial = useMemo(readQuery, []);
  const [panel, setPanel] = useState<Panel>(initial.panel);
  const [colours, setColours] = useState<Colours>(initial.colours);
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const timetable = useMemo(sampleTimetable, []);
  const events = useMemo(sampleEvents, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent || event.data?.type !== MESSAGE_TYPE) return;
      if (event.data.colours) setColours((current) => ({ ...current, ...cleanColours(event.data.colours) }));
      if (PANELS.includes(event.data.panel)) setPanel(event.data.panel);
    };
    const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("message", onMessage);
    window.addEventListener("resize", onResize);
    // Lets the admin page send any picks made before this frame finished loading.
    window.parent.postMessage({ type: `${MESSAGE_TYPE}-ready` }, "*");
    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const screenData: ScreenData = useMemo(() => {
    const data: ScreenData = { ...mockScreenData, salahChangesOrder: "DateOrder", use12HourFormat: true };
    (Object.keys(colours) as ColourKey[]).forEach((key) => {
      (data as unknown as Record<string, string>)[COLOUR_FIELDS[key]] = colours[key]!;
    });
    return data;
  }, [colours]);

  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden", background: "#000" }}>
      {panel === "salahChanges" && (
        <SimpleUpcomingSalahChangesSlideshow
          timetables={timetable}
          customizedNames={(screenData.customizedNames || {}) as unknown as Record<string, string>}
          duration={NEVER_ADVANCE}
          onComplete={noop}
          prayerTimes={timetable[0]}
          tomorrowPrayerTimes={timetable[1]}
          screenData={screenData}
          prevFridayJummah1={null}
          prevFridayJummah2={null}
          prevFridayJummah3={null}
          prevFridayJummah4={null}
          width={size.width}
          height={size.height}
        />
      )}

      {panel === "events" && (
        <SimpleUpcomingEventsWidget
          masjidId={screenData.masjidId}
          duration={NEVER_ADVANCE}
          onComplete={noop}
          screenData={screenData}
          width={size.width}
          height={size.height}
          sampleEvents={events}
        />
      )}

      {panel === "labbaik" && (
        <div style={{ width: "100%", height: "100%" }}>
          <LabbaikSlide item={SAMPLE_LABBAIK} colours={screenData} />
        </div>
      )}

      {panel === "footer" && (
        <div
          className="slideshow-page"
          style={
            {
              "--compact-primary-color": screenData.primaryColor,
              "--theme-primary-color": screenData.primaryColor,
              "--theme-secondary-color": screenData.secondaryColor,
              "--theme-accent-color": screenData.accentColor,
            } as React.CSSProperties
          }
        >
          <div
            className="slideshow-content"
            style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#666", fontSize: "3vw" }}
          >
            Slideshow posters show here
          </div>
          <div className="slideshow-jamaat-footer">
            <div className="footer-progress" style={{ width: "40%" }}></div>
            <div className="slideshow-counter">3 / 10&nbsp;</div>
            {SAMPLE_FOOTER.map(([label, time]) => (
              <span key={label} className="jamaat-time">
                <span className="jamaat-time-label">{label}</span>
                <strong>{time}</strong>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
